import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { DocumentService } from '@dealstack/frontend/shared/dataExchange/services/document/document.service';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { logger } from '@sentry/react';
import { PreviewDocument } from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/types';
import type { Dispatch, SetStateAction } from 'react';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { getFileExtensionFromMimeType } from '@dealstack/shared/lib/common/file/file';

type PreviewFileHandler = (args: {
	fileId: string;
	fileName: string;
	dealId: string;
	documentWorkflowInstanceId?: string;
}) => void;

export const usePreviewDocument = ({
	setPreviewDocument,
}: {
	setPreviewDocument: Dispatch<SetStateAction<PreviewDocument | null>>;
}): {
	handlePreviewFile: PreviewFileHandler;
	isLoading: boolean;
} => {
	const getDownloadPreSignedUrl = useMutation({
		mutationFn: DocumentService.getDownloadPreSignedUrl,
	});
	const tInvestor = useTranslationInvestor();
	const handlePreviewFile = useCallback(
		({
			fileId,
			fileName,
			dealId,
			documentWorkflowInstanceId,
		}: {
			fileId: string;
			fileName: string;
			dealId: string;
			documentWorkflowInstanceId?: string;
		}) => {
			getDownloadPreSignedUrl.mutate(
				{ dealId, fileId },
				{
					onSuccess: async (data) => {
						if (!data.payload?.downloadUrl) {
							showToastV2({
								title: tInvestor('investment.join.contracts.preview_error'),
								state: 'error',
							});
							logger.error('No download URL received for file preview:', {
								dealId,
								fileId,
							});
							return;
						}
						try {
							const response = await fetch(data.payload.downloadUrl);
							const blob = await response.blob();
							const contentType = response.headers.get('content-type') || '';
							const fileExtension = getFileExtensionFromMimeType(contentType);

							setPreviewDocument({
								fileId,
								fileName,
								fileBlob: blob,
								fileExtension,
								documentWorkflowInstanceId,
							});
						} catch {
							showToastV2({
								title: tInvestor('investment.join.contracts.preview_error'),
								state: 'error',
							});
							logger.error('Error fetching file blob for preview:', {
								dealId,
								fileId,
							});
						}
					},
					onError: () => {
						showToastV2({
							title: tInvestor('investment.join.contracts.preview_error'),
							state: 'error',
						});
						logger.error('Error fetching pre-signed URL for file preview:', {
							dealId,
							fileId,
						});
					},
				}
			);
		},
		[getDownloadPreSignedUrl, setPreviewDocument, tInvestor]
	);

	return {
		handlePreviewFile,
		isLoading: getDownloadPreSignedUrl.isPending,
	};
};
